import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import {
  LayoutDashboard, ShieldAlert, ListChecks, FolderOpen, Calendar as CalendarIcon,
  FileBarChart2, Send, BadgeCheck, Settings, Bell, LogOut, Crown, Sparkles, GraduationCap,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/risks", label: "Mappa Rischi", icon: ShieldAlert, testid: "nav-risks" },
  { to: "/controls", label: "Controlli", icon: ListChecks, testid: "nav-controls" },
  { to: "/documents", label: "Documenti", icon: FolderOpen, testid: "nav-documents" },
  { to: "/calendar", label: "Calendario", icon: CalendarIcon, testid: "nav-calendar" },
  { to: "/reports", label: "Report & Score", icon: FileBarChart2, testid: "nav-reports" },
  { to: "/ade", label: "Comunicazioni ADE", icon: Send, testid: "nav-ade" },
  { to: "/certification", label: "Certificazione", icon: BadgeCheck, testid: "nav-certification" },
  { to: "/ai-settings", label: "Motore AI", icon: Sparkles, testid: "nav-ai" },
  { to: "/pmi", label: "Guida PMI", icon: GraduationCap, testid: "nav-pmi" },
  { to: "/settings", label: "Impostazioni", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const { user, org, logout } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [tcfScore, setTcfScore] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/notifications").then(({ data }) => setNotifications(data || [])).catch(() => {});
    api.get("/dashboard/kpis").then(({ data }) => setTcfScore(data?.tcf_score)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user && org && !org.setup_completed) navigate("/onboarding");
  }, [user, org, navigate]);

  const unread = notifications.filter((n) => !n.is_read).length;

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-950 text-slate-300 flex flex-col border-r border-slate-900" data-testid="main-sidebar">
        <div className="px-5 py-5 border-b border-slate-900">
          <div className="font-display font-bold text-white tracking-tight text-xl">TCF<span className="text-slate-500">.</span>Platform</div>
          <div className="label-tag mt-1 text-slate-500">Tax Control Framework</div>
        </div>

        <div className="px-3 py-4 border-b border-slate-900">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Organizzazione</div>
          <div className="text-sm font-medium text-white truncate mt-1" data-testid="sidebar-org-name">{org?.name || "—"}</div>
          {org?.is_pmi && <Badge variant="outline" className="mt-2 text-[10px] border-slate-700 text-amber-300">Regime PMI</Badge>}
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={it.testid}
              className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link-active" : ""}`}
            >
              <it.icon size={16} strokeWidth={1.8} />
              <span>{it.label}</span>
            </NavLink>
          ))}
          {user?.role === "super_admin" && (
            <NavLink to="/admin" data-testid="nav-admin" className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link-active" : ""}`}>
              <Crown size={16} strokeWidth={1.8} />
              <span>Super Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="border-t border-slate-900 px-3 py-3">
          <button onClick={logout} data-testid="logout-btn" className="sidebar-link w-full">
            <LogOut size={16} strokeWidth={1.8} />
            <span>Esci</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-8 py-3 flex items-center justify-between sticky top-0 z-10" data-testid="main-header">
          <div className="flex items-center gap-6">
            <div className="label-tag">Profilo TCF</div>
            <Badge variant="outline" className="border-slate-300 text-slate-700 rounded-sm">
              {user?.role?.replace("_", " ")}
            </Badge>
            {tcfScore !== null && (
              <div className="flex items-center gap-2" data-testid="header-tcf-score">
                <span className="label-tag">TCF Score</span>
                <span className="data-num text-lg font-semibold text-slate-900">{tcfScore}</span>
                <span className="text-xs text-slate-500">/100</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-sm border-slate-300 relative" data-testid="notifications-bell">
                  <Bell size={16} />
                  {unread > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{unread}</span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Notifiche</SheetTitle>
                </SheetHeader>
                <div className="mt-4 space-y-2">
                  {notifications.length === 0 && <div className="text-sm text-slate-500">Nessuna notifica</div>}
                  {notifications.map((n) => (
                    <div key={n.id} className="card-flat p-3 text-sm">
                      <div className="font-medium text-slate-900">{n.title}</div>
                      <div className="text-slate-600 text-xs mt-1">{n.message}</div>
                    </div>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
            <div className="text-right">
              <div className="text-sm font-medium text-slate-900" data-testid="header-user-name">{user?.full_name}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
          </div>
        </header>
        <div className="flex-1 p-8 fade-up">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
